import { useState } from "react";
import {
  FaEnvelopeOpenText,
  FaFilePdf,
  FaSearch,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import {
  downloadWelcomeLetterPdf,
  fetchWelcomeLetterPreview,
  validateWelcomeLetterLan,
} from "../services/welcomeLetterApi";

const previewFields = [
  ["Customer Name", "customerName"],
  ["Loan Product", "loanProduct"],
  ["Group Name", "groupName"],
  ["Group Coordinator Details", "groupCoordinatorDetails"],
  ["Loan Account Number", "loanAccountNumber"],
  ["Loan Amount", "loanAmount"],
  ["Mobile Number", "mobileNumber"],
  ["EMI Amount", "emiAmount"],
  ["First EMI Date", "firstEmiDate"],
  ["Last EMI Date", "lastEmiDate"],
];

function WelcomeLetterPage() {
  usePageTitle("Welcome Letter Generation | Pargro");
  const [loanAccountNumber, setLoanAccountNumber] = useState("");
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleSearch = async (event) => {
    event.preventDefault();
    setSelectedLoan(null);
    setError("");
    setStatusMessage("");

    let validatedLoanAccountNumber;

    try {
      validatedLoanAccountNumber = validateWelcomeLetterLan(loanAccountNumber);
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    setIsSearching(true);
    setStatusMessage("Searching for loan details...");

    try {
      const preview = await fetchWelcomeLetterPreview(
        validatedLoanAccountNumber,
      );
      setSelectedLoan(preview);
      setLoanAccountNumber(preview.loanAccountNumber);
      setStatusMessage("Loan details loaded successfully.");
    } catch (searchError) {
      setError(
        searchError.message || "Unable to retrieve Welcome Letter details.",
      );
      setStatusMessage("");
    } finally {
      setIsSearching(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedLoan?.downloadAvailable || isDownloading) {
      return;
    }

    setError("");
    setStatusMessage("Generating PDF...");
    setIsDownloading(true);

    try {
      await downloadWelcomeLetterPdf(selectedLoan.loanAccountNumber);
      setStatusMessage("Welcome Letter PDF downloaded successfully.");
    } catch (downloadError) {
      setError(
        downloadError.message ||
          "Unable to generate the Welcome Letter PDF.",
      );
      setStatusMessage("");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ActionPageLayout
      title="Welcome Letter Generation Portal"
      icon={
        <FaEnvelopeOpenText
          className="h-6 w-6 flex-none text-[#198754]"
          aria-hidden="true"
        />
      }
    >
      <section className="mx-auto mt-12 w-full max-w-[1300px] max-[720px]:mt-6">
        <form
          className="grid grid-cols-[minmax(0,2fr)_minmax(12rem,1fr)] gap-2 rounded-lg border border-[#d7dce3] bg-white p-4 shadow-[0_5px_16px_rgba(15,23,42,0.16)] max-[720px]:grid-cols-1"
          onSubmit={handleSearch}
        >
          <label className="sr-only" htmlFor="welcome-letter-loan-account">
            Loan account number
          </label>
          <input
            autoComplete="off"
            className="h-[40px] min-w-0 rounded-md border border-[#d4dae3] bg-white px-3 text-base outline-none transition placeholder:text-[#6b4b3e] focus:border-[#0d73ff] focus:shadow-[0_0_0_3px_rgba(13,115,255,0.16)]"
            id="welcome-letter-loan-account"
            inputMode="numeric"
            maxLength={20}
            placeholder="Enter Loan Account Number"
            value={loanAccountNumber}
            onChange={(event) => {
              setLoanAccountNumber(event.target.value);
              setError("");
              setStatusMessage("");
            }}
          />
          <button
            className="inline-flex h-[40px] items-center justify-center gap-2 rounded-md bg-[#176ff2] px-6 text-base font-medium text-white transition hover:bg-[#0d5fd6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] disabled:cursor-not-allowed disabled:bg-[#8bbcfb]"
            disabled={isSearching}
            type="submit"
          >
            <FaSearch className="h-4 w-4" aria-hidden="true" />
            {isSearching ? "Searching..." : "Search Loan"}
          </button>
        </form>

        {statusMessage ? (
          <div
            className="mt-6 rounded-md border border-[#badbcc] bg-[#d1e7dd] px-4 py-3 text-center text-sm text-[#0f5132]"
            role="status"
          >
            {statusMessage}
          </div>
        ) : null}

        {error ? (
          <div
            className="mt-6 rounded-md border border-[#f1aeb5] bg-[#f8d7da] px-4 py-4 text-center text-sm text-[#842029]"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {selectedLoan ? (
          <section className="mt-6 overflow-hidden rounded-lg border border-[#d7dce3] bg-white shadow-[0_5px_16px_rgba(15,23,42,0.16)]">
            <h2 className="m-0 bg-[#198754] px-4 py-3 text-base font-medium text-white">
              Loan Preview
            </h2>
            <dl className="grid grid-cols-[minmax(12rem,1fr)_minmax(15rem,1fr)] gap-x-8 gap-y-3 px-4 py-5 text-base max-[720px]:grid-cols-1 max-[720px]:gap-y-1">
              {previewFields.map(([label, key]) => (
                <div className="contents max-[720px]:block" key={key}>
                  <dt className="font-bold text-[#111827]">{label}:</dt>
                  <dd className="m-0 break-words text-[#111827] max-[720px]:mb-3">
                    {selectedLoan[key]}
                  </dd>
                </div>
              ))}
            </dl>
            {selectedLoan.downloadAvailable ? (
              <div className="px-4 pb-4">
                <button
                  className="inline-flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-[#198754] px-6 text-base font-medium text-white transition hover:bg-[#146c43] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8fd5b1] disabled:cursor-not-allowed disabled:bg-[#75b798]"
                  disabled={isDownloading}
                  type="button"
                  onClick={handleDownload}
                >
                  <FaFilePdf className="h-4 w-4" aria-hidden="true" />
                  {isDownloading
                    ? "Generating PDF..."
                    : "Download Welcome Letter PDF"}
                </button>
              </div>
            ) : null}
          </section>
        ) : null}
      </section>
    </ActionPageLayout>
  );
}

export default WelcomeLetterPage;
